import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BusinessSettingsLayout } from '@/components/business-settings/BusinessSettingsLayout';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { useAttendanceSettings } from '@/hooks/attendance';
import { useMyProfile } from '@/hooks/profile';
import { Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { safeParseInt } from '@/utils/validation';
import { useToast } from '@/hooks/use-toast';
import { SplitShiftSettingsForm } from '@/components/business-settings/SplitShiftSettingsForm';
import { useCompanySettings } from '@/hooks/useCompanySettings';

export default function TimeAttendanceSettings() {
  const { data: profile } = useMyProfile();
  const { data: settings, isLoading, updateSettings, isUpdating } = useAttendanceSettings(profile?.company_id);
  const { settings: companySettings, updateSettings: updateCompanySettings } = useCompanySettings();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    early_checkin_threshold_minutes: settings?.early_checkin_threshold_minutes ?? 15,
    late_checkin_threshold_minutes: settings?.late_checkin_threshold_minutes ?? 15,
    early_checkout_threshold_minutes: settings?.early_checkout_threshold_minutes ?? 15,
    late_checkout_threshold_minutes: settings?.late_checkout_threshold_minutes ?? 15,
    allow_unscheduled_checkin: settings?.allow_unscheduled_checkin ?? true,
    flag_unscheduled_checkin: settings?.flag_unscheduled_checkin ?? true,
    flag_missed_checkin: settings?.flag_missed_checkin ?? true,
    flag_missed_checkout: settings?.flag_missed_checkout ?? true,
    missed_punch_grace_period_minutes: settings?.missed_punch_grace_period_minutes ?? 30,
    enable_realtime_flagging: settings?.enable_realtime_flagging ?? true,
    enable_nightly_detection: settings?.enable_nightly_detection ?? true,
  });

  // Update form when settings load
  useEffect(() => {
    if (settings) {
      setFormData({
        early_checkin_threshold_minutes: settings.early_checkin_threshold_minutes,
        late_checkin_threshold_minutes: settings.late_checkin_threshold_minutes,
        early_checkout_threshold_minutes: settings.early_checkout_threshold_minutes,
        late_checkout_threshold_minutes: settings.late_checkout_threshold_minutes,
        allow_unscheduled_checkin: settings.allow_unscheduled_checkin,
        flag_unscheduled_checkin: settings.flag_unscheduled_checkin,
        flag_missed_checkin: settings.flag_missed_checkin,
        flag_missed_checkout: settings.flag_missed_checkout,
        missed_punch_grace_period_minutes: settings.missed_punch_grace_period_minutes,
        enable_realtime_flagging: settings.enable_realtime_flagging,
        enable_nightly_detection: settings.enable_nightly_detection,
      });
    }
  }, [settings]);

  const handleSplitShiftSubmit = async (data: any) => {
    try {
      await updateCompanySettings({ split_shift_config: data });
      toast({
        title: "Settings Saved",
        description: "Split-shift detection settings have been updated successfully.",
      });
    } catch (error) {
      console.error('[TimeAttendanceSettings] Error saving split-shift settings:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save split-shift settings.",
        variant: "destructive",
      });
    }
  };

  const handleSave = () => {
    if (!profile?.company_id) {
      toast({
        title: 'Cannot save',
        description: 'Company information is not available.',
        variant: 'destructive',
      });
      return;
    }
    
    // Pre-save sanitization - defense in depth against any NaN values
    // Note: missed_punch_grace_period_minutes can be null (backend defaults to 30)
    const sanitizedData = {
      ...formData,
      early_checkin_threshold_minutes: Math.max(0, formData.early_checkin_threshold_minutes ?? 15),
      late_checkin_threshold_minutes: Math.max(0, formData.late_checkin_threshold_minutes ?? 15),
      early_checkout_threshold_minutes: Math.max(0, formData.early_checkout_threshold_minutes ?? 15),
      late_checkout_threshold_minutes: Math.max(0, formData.late_checkout_threshold_minutes ?? 15),
      missed_punch_grace_period_minutes: formData.missed_punch_grace_period_minutes != null 
        ? Math.max(0, formData.missed_punch_grace_period_minutes) 
        : null,
    };
    updateSettings(sanitizedData);
  };

  return (
    <BusinessSettingsLayout
      title="Time & Attendance"
      description="Configure time tracking, attendance policies, and overtime rules"
    >
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-6">
        {/* Check-in/out Thresholds */}
        <Card>
          <CardHeader>
            <CardTitle>Check-in/out Thresholds</CardTitle>
            <CardDescription>
              Define how early or late employees can check in/out before being flagged
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="early_checkin">Early Check-in Threshold (minutes)</Label>
                <Input
                  id="early_checkin"
                  type="number"
                  min="0"
                  value={formData.early_checkin_threshold_minutes}
                  onChange={(e) =>
                    setFormData({ ...formData, early_checkin_threshold_minutes: safeParseInt(e.target.value, 15) })
                  }
                />
                <p className="text-sm text-muted-foreground">
                  Flag if employee checks in more than this many minutes early
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="late_checkin">Late Check-in Threshold (minutes)</Label>
                <Input
                  id="late_checkin"
                  type="number"
                  min="0"
                  value={formData.late_checkin_threshold_minutes}
                  onChange={(e) =>
                    setFormData({ ...formData, late_checkin_threshold_minutes: safeParseInt(e.target.value, 15) })
                  }
                />
                <p className="text-sm text-muted-foreground">
                  Flag if employee checks in more than this many minutes late
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="early_checkout">Early Check-out Threshold (minutes)</Label>
                <Input
                  id="early_checkout"
                  type="number"
                  min="0"
                  value={formData.early_checkout_threshold_minutes}
                  onChange={(e) =>
                    setFormData({ ...formData, early_checkout_threshold_minutes: safeParseInt(e.target.value, 15) })
                  }
                />
                <p className="text-sm text-muted-foreground">
                  Flag if employee checks out more than this many minutes early
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="late_checkout">Late Check-out Threshold (minutes)</Label>
                <Input
                  id="late_checkout"
                  type="number"
                  min="0"
                  value={formData.late_checkout_threshold_minutes}
                  onChange={(e) =>
                    setFormData({ ...formData, late_checkout_threshold_minutes: safeParseInt(e.target.value, 15) })
                  }
                />
                <p className="text-sm text-muted-foreground">
                  Flag if employee checks out more than this many minutes late
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Unscheduled Day Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Unscheduled Day Settings</CardTitle>
            <CardDescription>
              Configure how to handle check-ins on days without scheduled shifts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Allow Unscheduled Check-ins</Label>
                <p className="text-sm text-muted-foreground">
                  Permit employees to check in on days they're not scheduled
                </p>
              </div>
              <Switch
                checked={formData.allow_unscheduled_checkin}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, allow_unscheduled_checkin: checked })
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Flag Unscheduled Check-ins</Label>
                <p className="text-sm text-muted-foreground">
                  Create a flag when employees check in on unscheduled days
                </p>
              </div>
              <Switch
                checked={formData.flag_unscheduled_checkin}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, flag_unscheduled_checkin: checked })
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Detection Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Detection Settings</CardTitle>
            <CardDescription>
              Configure when and how attendance flags are generated
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Real-time Flagging</Label>
                <p className="text-sm text-muted-foreground">
                  Create flags immediately when employees check in/out
                </p>
              </div>
              <Switch
                checked={formData.enable_realtime_flagging}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, enable_realtime_flagging: checked })
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Nightly Detection Job</Label>
                <p className="text-sm text-muted-foreground">
                  Run automated detection daily for missed check-ins and check-outs
                </p>
              </div>
              <Switch
                checked={formData.enable_nightly_detection}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, enable_nightly_detection: checked })
                }
              />
            </div>

            {/* Missed Punch Detection - Only visible when Nightly Detection is enabled */}
            {formData.enable_nightly_detection && (
              <div className="space-y-4 pt-4 border-t">
                <div className="space-y-1">
                  <Label className="text-base font-medium">Missed Punch Detection</Label>
                  <p className="text-sm text-muted-foreground">
                    Configure automatic detection of missed check-ins and check-outs
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Flag Missed Check-ins</Label>
                    <p className="text-sm text-muted-foreground">
                      Alert when scheduled employees don't check in
                    </p>
                  </div>
                  <Switch
                    checked={formData.flag_missed_checkin}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, flag_missed_checkin: checked })
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Flag Missed Check-outs</Label>
                    <p className="text-sm text-muted-foreground">
                      Alert when employees forget to check out
                    </p>
                  </div>
                  <Switch
                    checked={formData.flag_missed_checkout}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, flag_missed_checkout: checked })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="grace_period">Grace Period (minutes)</Label>
                  <Input
                    id="grace_period"
                    type="number"
                    min="0"
                    value={formData.missed_punch_grace_period_minutes ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData({ ...formData, missed_punch_grace_period_minutes: val === '' ? null : Math.max(0, parseInt(val, 10) || 0) });
                    }}
                  />
                  <p className="text-sm text-muted-foreground">
                    Wait this long after scheduled time before flagging as missed
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

          {/* Split-Shift Detection */}
          <SplitShiftSettingsForm
            initialData={companySettings?.split_shift_config || {
              enabled: true,
              minimumGapMinutes: 60,
              minimumShiftLengthMinutes: 30,
              countMultiplePerDay: true
            }}
            onSubmit={handleSplitShiftSubmit}
            isLoading={false}
          />

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={isUpdating || !profile?.company_id}>
              {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Settings
            </Button>
          </div>
        </div>
      )}
    </BusinessSettingsLayout>
  );
}
